import React, { useMemo, useState } from "react";
import MenuComponent from "../../components/MenuComponent/MenuComponent";
import Chart from "react-apexcharts";
import { tahun } from "../../util/constant";
import "./Dashboard.css";
import { useDashboard } from "./useDashboard";

// Palet warna tetap (tidak acak lagi), supaya warna konsisten setiap render
const PALETTE = [
  "#4F46E5",
  "#06B6D4",
  "#10B981",
  "#F59E0B",
  "#EF4444",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
  "#F97316",
  "#64748B",
];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Agu",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const formatNumber = (val) =>
  new Intl.NumberFormat("id-ID").format(Number(val) || 0);

const escapeHtml = (str) =>
  String(str).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[
        c
      ])
  );

// Hitung perubahan bulan ini dibanding bulan sebelumnya
const getDelta = (curr, prev) => {
  const c = Number(curr) || 0;
  const p = Number(prev) || 0;
  const diff = c - p;
  let pct = null;
  if (p > 0) pct = (diff / p) * 100;
  const dir = diff > 0 ? "up" : diff < 0 ? "down" : "same";
  return { diff, pct, dir };
};

const formatDelta = ({ diff, pct, dir }) => {
  if (dir === "same") return "= 0";
  const arrow = dir === "up" ? "▲" : "▼";
  const sign = diff > 0 ? "+" : "";
  const pctText =
    pct === null ? " (baru)" : ` (${sign}${pct.toFixed(1).replace(".", ",")}%)`;
  return `${arrow} ${sign}${formatNumber(diff)}${pctText}`;
};

const DELTA_COLOR = { up: "#16A34A", down: "#DC2626", same: "#64748B" };

const Dashboard = () => {
  const { filterTahun, setFilterTahun, listBpb } = useDashboard();
  const dataAuth = localStorage.getItem("auth");
  const dataUser = JSON.parse(dataAuth);
  const [menu, setMenu] = useState("dashboard");

  const series = listBpb || [];
  const hasData = series.length > 0;

  // Ringkasan total untuk kartu di atas chart
  const summary = useMemo(() => {
    const monthlyTotals = MONTHS.map((_, i) =>
      series.reduce((sum, s) => sum + (Number(s.data?.[i]) || 0), 0)
    );
    const total = monthlyTotals.reduce((a, b) => a + b, 0);
    const maxValue = Math.max(...monthlyTotals, 0);
    const peakIndex = monthlyTotals.indexOf(maxValue);
    return {
      total,
      peakMonth: maxValue > 0 ? MONTHS[peakIndex] : "-",
      peakValue: maxValue,
      average: Math.round(total / 12),
    };
  }, [series]);

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        fontFamily: "Inter, Segoe UI, Roboto, sans-serif",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false,
          },
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 600,
          animateGradually: { enabled: true, delay: 80 },
        },
        dropShadow: { enabled: false },
      },
      colors: PALETTE,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "55%",
          borderRadius: 6,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          dataLabels: {
            total: {
              enabled: true,
              offsetY: -8,
              style: {
                fontSize: "13px",
                fontWeight: 700,
                color: "#1E293B",
              },
              formatter: (val) => (val > 0 ? formatNumber(val) : ""),
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: "12px", fontWeight: 600, colors: ["#fff"] },
        // sembunyikan label angka 0 supaya chart tidak berantakan
        formatter: (val) => (val > 0 ? formatNumber(val) : ""),
        dropShadow: { enabled: false },
      },
      stroke: { show: true, width: 1.5, colors: ["#fff"] },
      xaxis: {
        categories: MONTHS,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { fontSize: "13px", fontWeight: 500, colors: "#475569" },
        },
      },
      yaxis: {
        labels: {
          style: { fontSize: "12px", colors: "#64748B" },
          formatter: (val) => formatNumber(val),
        },
      },
      grid: {
        borderColor: "#E2E8F0",
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 10, right: 360, left: 10 },
      },
      title: {
        text: `Total BPB ${filterTahun}`,
        align: "left",
        style: { fontSize: "20px", fontWeight: 700, color: "#0F172A" },
      },
      legend: {
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 500,
        markers: { width: 10, height: 10, radius: 10 },
        itemMargin: { horizontal: 10, vertical: 4 },
      },
      tooltip: {
        theme: "light",
        fixed: { enabled: true, position: "topRight", offsetX: 0, offsetY: 60 },
        shared: true,
        intersect: false,
        custom: ({ series: s, dataPointIndex, w }) => {
          const rows = s
            .map((row, i) => {
              const curr = row[dataPointIndex];
              if (!(Number(curr) > 0) && dataPointIndex === 0) return "";
              const name = escapeHtml(w.globals.seriesNames[i]);
              const color = w.globals.colors[i];
              let deltaHtml = "";
              if (dataPointIndex > 0) {
                const d = getDelta(curr, row[dataPointIndex - 1]);
                deltaHtml = `<span style="color:${
                  DELTA_COLOR[d.dir]
                };font-weight:600;margin-left:8px">${formatDelta(d)}</span>`;
              }
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:16px;padding:6px 0">
                <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px"></span>${name}</span>
                <span><b>${formatNumber(curr)}</b>${deltaHtml}</span>
              </div>`;
            })
            .join("");
          const totalCurr = s.reduce(
            (a, r) => a + (Number(r[dataPointIndex]) || 0),
            0
          );
          let totalDelta = "";
          if (dataPointIndex > 0) {
            const totalPrev = s.reduce(
              (a, r) => a + (Number(r[dataPointIndex - 1]) || 0),
              0
            );
            const d = getDelta(totalCurr, totalPrev);
            totalDelta = `<span style="color:${
              DELTA_COLOR[d.dir]
            };font-weight:600;margin-left:8px">${formatDelta(d)}</span>`;
          }
          return `<div style="padding:14px 18px;min-width:300px;font-size:13px">
            <div style="font-weight:700;font-size:14px;margin-bottom:10px">${
              MONTHS[dataPointIndex]
            } ${filterTahun}</div>
            ${rows}
            <div style="border-top:1px solid #E2E8F0;margin-top:10px;padding-top:10px;display:flex;justify-content:space-between">
              <b>Total</b><span><b>${formatNumber(
                totalCurr
              )}</b>${totalDelta}</span>
            </div>
            ${
              dataPointIndex > 0
                ? '<div style="margin-top:4px;color:#94A3B8;font-size:11px">Perubahan dibanding bulan sebelumnya</div>'
                : ""
            }
          </div>`;
        },
      },
      states: {
        hover: { filter: { type: "lighten", value: 0.08 } },
        active: { filter: { type: "none" } },
      },
      noData: {
        text: "Tidak ada data",
        style: { fontSize: "16px", color: "#64748B" },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            plotOptions: { bar: { columnWidth: "75%" } },
            grid: { padding: { top: 10, right: 10, left: 10 } },
            tooltip: { fixed: { enabled: false } },
            dataLabels: { enabled: false },
            legend: { position: "bottom", horizontalAlign: "center" },
            title: { style: { fontSize: "16px" } },
          },
        },
      ],
    }),
    [filterTahun]
  );

  return (
    <>
      {dataUser !== null ? <MenuComponent setMenu={setMenu} menu={menu} /> : ""}
      <div className="container_dashboard">
        <div className="wrapper-dashboard">
          <div className="dashboard-header">
            <div>
              <h2 className="dashboard-title">Dashboard BPB</h2>
              <p className="dashboard-subtitle">
                Rekap BPB per bulan tahun {filterTahun}
              </p>
            </div>
            <select
              className="dashboard-select"
              value={filterTahun}
              onChange={(e) => setFilterTahun(e.target.value)}
            >
              <option disabled>Pilih Tahun</option>
              {tahun.map((ele) => (
                <option key={ele} value={ele}>
                  {ele}
                </option>
              ))}
            </select>
          </div>

          <div className="summary-grid">
            <div className="summary-card">
              <span className="summary-label">Total Setahun</span>
              <span className="summary-value">
                {formatNumber(summary.total)}
              </span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Rata-rata / Bulan</span>
              <span className="summary-value">
                {formatNumber(summary.average)}
              </span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Bulan Tertinggi</span>
              <span className="summary-value">
                {summary.peakMonth}
                {summary.peakValue > 0 && (
                  <small> ({formatNumber(summary.peakValue)})</small>
                )}
              </span>
            </div>
          </div>

          <div className="chart-card">
            {hasData ? (
              <Chart
                type="bar"
                height={560}
                series={series}
                options={options}
              />
            ) : (
              <div className="chart-empty">Memuat data...</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
